import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/media";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { RevealOnScroll } from "@/components/site/Reveal";

export default function VehicleDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [wh, setWh] = useState("");

  useEffect(() => {
    api.get(`/vehicles/${slug}`).then((r) => setV(r.data)).catch(() => setV(false));
    api.get("/site/config").then((r) => setWh(r.data.whatsapp)).catch(() => {});
  }, [slug]);

  if (v === null) return <div className="pt-40 text-center text-white/60">Loading…</div>;
  if (v === false) return <div className="pt-40 text-center text-white/60">Vehicle not found.</div>;

  const price = new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS", maximumFractionDigits: 0 }).format(v.price_ghs);
  const waMsg = encodeURIComponent(`Hello Maklan Motors, I'm interested in the ${v.year} ${v.make} ${v.model}. Please share more details.`);
  const waLink = wh ? `https://wa.me/${wh.replace(/[^0-9]/g,"")}?text=${waMsg}` : "#";

  return (
    <div data-testid="vehicle-detail-page" className="pt-32 pb-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Link to="/inventory" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/60 hover:text-[#C5A059] transition mb-10">
          <ArrowLeft size={14} /> Back to Inventory
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="vehicle-frame">
              <motion.img key={active} src={v.images[active]} alt={v.model}
                data-testid="vehicle-main-image"
                initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.22,1,0.36,1] }}
              />
            </div>
            {v.images.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {v.images.map((im, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-video overflow-hidden border transition-all ${active === i ? "border-[#C5A059]" : "border-white/10 opacity-60 hover:opacity-100"}`}>
                    <img src={resolveImg(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="overline mb-4">{v.year} · {v.type === "ev" ? "Electric" : v.type === "hybrid" ? "Plug-in Hybrid" : "Petrol"}</div>
            <RevealOnScroll
              element="h1"
              className="h-editorial text-5xl md:text-6xl"
              lines={[v.make, `<em>${v.model}</em>`]}
            />
            <p className="mt-6 text-white/70 leading-relaxed">{v.tagline}</p>

            <div className="mt-8 flex items-baseline gap-4">
              <span className="font-display text-4xl">{price}</span>
              <span className="text-xs tracking-[0.2em] uppercase text-white/40">Shipping included</span>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-white/10 pt-8">
              {v.specs.map((s) => (
                <div key={s.label}>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">{s.label}</div>
                  <div className="mt-1 font-display text-lg">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 border-t border-white/10 pt-8">
              <div className="overline mb-4">Highlights</div>
              <ul className="space-y-2">
                {v.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-white/80">
                    <span className="text-[#C5A059] mt-1.5">◆</span> {h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={`/reserve?vehicle=${v.slug}`} className="btn-gold" data-testid="detail-cta-reserve">Reserve</Link>
              <Link to={`/quote?vehicle=${v.slug}`} className="btn-ghost" data-testid="detail-cta-quote">Request Quote</Link>
              <Link to={`/compare?add=${v.slug}`} className="btn-ghost" data-testid="detail-cta-compare">Compare</Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="detail-cta-whatsapp">
                <MessageCircle className="mr-2" size={14}/> WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-32 grid lg:grid-cols-12 gap-16 border-t border-white/10 pt-20">
          <div className="lg:col-span-4"><div className="overline">About this car</div></div>
          <div className="lg:col-span-8">
            <p className="font-display text-2xl md:text-3xl leading-snug text-white/85">
              {v.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
